import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ApiError, useToolsApi } from '../apis/tools-apis';
import { Tool } from '../types';
import { useToast } from "../components/ui/Toast";
import { Spinner } from "../components/ui/Spinner";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { ArrowLeft, Pencil } from 'lucide-react';
import ToolForm, { ToolFormRef } from './ToolForm';
import { NavigationWarningDialog } from '../components/NavigationWarningDialog';
import { useNavigation } from '../contexts/NavigationContext';
import { useAuth } from '../contexts/AuthContext';
import { DEFAULT_TOOL_TIMEOUT } from '../config/api-config';

// Define a more specific type for the location state
type LocationState = {
  startEditing?: boolean;
  from?: string;
};

export const EditToolPage: React.FC = () => {
  const formRef = useRef<ToolFormRef>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const toolsApi = useToolsApi();
  const [editedTool, setEditedTool] = useState<Tool | null>(null);
  const [originalTool, setOriginalTool] = useState<Tool | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(() => {
    return !(location.state as { startEditing?: boolean })?.startEditing;
  });
  const initialFetchMade = useRef(false);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [existingToolNames, setExistingToolNames] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const backButtonClicked = useRef(false);
  const historyStateAdded = useRef(false);
  const { user } = useAuth();
  const { 
    showNavigationWarning, 
    pendingNavigation, 
    setShowNavigationWarning, 
    setPendingNavigation 
  } = useNavigation();
  
  const hasUnsavedChanges = !isReadOnly && 
    JSON.stringify(editedTool) !== JSON.stringify(originalTool);

  // Handle browser back button
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    const handlePopState = (e: PopStateEvent) => {
      // Only handle if there are unsaved changes
      if (hasUnsavedChanges) {
        backButtonClicked.current = true;
        setShowNavigationWarning(true);
        setPendingNavigation('/studio/tools');
      } else {
        // If no unsaved changes, allow normal back navigation
        navigate('/studio/tools');
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    // Add a single history state entry if not already added
    if (!historyStateAdded.current && hasUnsavedChanges) {
      window.history.pushState(null, '', window.location.pathname);
      historyStateAdded.current = true;
    }

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, navigate, setShowNavigationWarning, setPendingNavigation]);

  useEffect(() => {
    const fetchToolAndNames = async () => {
      if (id && !initialFetchMade.current) {
        initialFetchMade.current = true;
        try {
          const getAllTools = true;
          const tool = await toolsApi.getTool(id);
          const allTools = await toolsApi.getTools(getAllTools);

          setExistingToolNames(
            allTools
              .filter(s => s.toolId !== id)
              .map(s => s.toolName)
          );
          
          const allTags = new Set<string>();
          allTools.forEach(tool => {
            tool.toolTags?.forEach(tag => {
              if (tag) allTags.add(tag);
            });
          });
          
          setTagSuggestions(Array.from(allTags));
  
          if (tool) {
            const updatedTool = {
              ...tool,
              toolTags: tool.toolTags || [],
              toolTimeout: tool.toolTimeout || DEFAULT_TOOL_TIMEOUT
            };
            setEditedTool(updatedTool);
            setOriginalTool(updatedTool);
          } else {
            addToast({
              title: "Error",
              description: "Tool not found",
              variant: "destructive",
              duration: 4000
            });
            navigate('/studio/tools');
          }
        } catch (error: any) {
          console.error('Error fetching data:', error);
          
          if (error instanceof ApiError && error.status === 403) {
            navigate('/studio/unauthorized');
            return;
          }
          
          // For all other errors, show the specific error message or fallback
          const errorMessage = error instanceof ApiError 
            ? error.message 
            : "Failed to fetch tool details";
            
          addToast({
            title: "Error",
            description: errorMessage,
            variant: "destructive",
            duration: 5000
          });
          navigate('/studio/tools');
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchToolAndNames();
  }, [id, addToast, navigate, toolsApi]);

  const handleSubmit = async () => {
    if (!editedTool) {
      addToast({
        title: "Error",
        description: "No tool data available to save",
        variant: "destructive",
        duration: 4000
      });
      return;
    }
  
    const isValid = await formRef.current?.validateForm();
    if (!isValid) {
      addToast({
        title: "Validation Error",
        description: "Please fill in all required fields correctly",
        variant: "destructive",
        duration: 4000
      });
      return;
    }
  
    setIsSaving(true);
  
    try {
      const toolToUpdate = {
        ...editedTool,
        toolName: editedTool.toolName.trim(),
        toolDescription: editedTool.toolDescription.trim(),
        toolUrlEndpoint: editedTool.toolUrlEndpoint.trim(),
        toolTimeout: editedTool.toolTimeout || DEFAULT_TOOL_TIMEOUT,
        toolParameters: {
          parameters: editedTool.toolParameters?.parameters || [],
          testMode: editedTool.toolParameters?.testMode ?? true
        },
        toolOutput: editedTool.toolOutput || { parameters: {} },
        toolAnnotation: editedTool.toolAnnotation || '',
        toolTags: editedTool.toolTags || [],  // Ensure tags are initialized
        isCore: editedTool.isCore ?? false // This is currently not set in the UI
      };
  
      if (editedTool.toolAuthentication && editedTool.toolAuthentication !== '••••••••') {
        toolToUpdate.toolAuthentication = editedTool.toolAuthentication.trim();
      }
  
      const updatedTool = await toolsApi.updateTool(toolToUpdate);
      
      // Ensure tags are initialized in the response
      const normalizedTool = {
        ...updatedTool,
        toolTags: updatedTool.toolTags || []
      };
      
      setOriginalTool(normalizedTool);
      setEditedTool(normalizedTool);
      setIsReadOnly(true);
      
      addToast({
        title: "Success",
        description: `Tool ${toolToUpdate.toolName} has been successfully updated`,
        variant: "default",
        duration: 4000
      });
  
      if (pendingNavigation) {
        navigate(pendingNavigation);
        setPendingNavigation(null);
      } else {
        navigate(`/studio/tools/test/${toolToUpdate.toolId}`);
      }
    } catch (error) {
      console.error('Error updating tool:', error);
      addToast({
        title: "Error",
        description: "Failed to update tool. Please try again.",
        variant: "destructive",
        duration: 4000
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation(null);
    } else {
      setEditedTool(originalTool);
      setIsReadOnly(true);
    }
  };

  const handleConfirmNavigation = () => {
    setShowNavigationWarning(false);
    setEditedTool(originalTool);
    setIsReadOnly(true);
    
    if (backButtonClicked.current) {
      backButtonClicked.current = false;
      historyStateAdded.current = false;
      navigate('/studio/tools');
    } else if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
    backButtonClicked.current = false;
    
    // If this was a back button navigation, restore the history state
    if (backButtonClicked.current) {
      window.history.pushState(null, '', window.location.pathname);
    }
  };

  const handleNavigateBack = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation('/studio/tools');
    } else {
      navigate('/studio/tools');
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center my-8 gap-2">
        <Spinner className="border-blue-500" />
        <p className="text-center text-muted-foreground">Loading Tool...</p>
      </div>
    );
  }

  if (!editedTool) {
    return (
      <Card className="max-w-2xl mx-auto m-4">
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">Tool not found.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-4" data-has-unsaved-changes={hasUnsavedChanges}>
      {/* Back Button - Positioned absolutely */}
      <div className="absolute left-4 top-4">
        <Button
          variant="ghost"
          onClick={handleNavigateBack}
          size="sm"
          className="px-2 sm:px-3"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="sr-only sm:not-sr-only sm:ml-2">Back</span>
        </Button>
      </div>

      {/* Main Content */}
      <div className="pt-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1 min-w-0">
            <div className="overflow-hidden">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {isReadOnly ? 'View Tool' : 'Edit Tool'}
              </h1>
              <p className="text-sm font-medium text-gray-500 text-muted-foreground mt-1">
                {isReadOnly 
                  ? 'View and test this tool configuration.'
                  : 'Update the tool details and configuration.'}
              </p>
            </div>
          </div>

          {isReadOnly && (
            <div className="mt-4 sm:mt-0">
              <Button 
                onClick={() => setIsReadOnly(false)}
                size="sm"
                className="w-full sm:w-auto"
              >
                <Pencil className="mr-2 h-4 w-4" />
                <span>Edit Tool</span>
              </Button>
            </div>
          )}
        </div>
      </div>

      <ToolForm 
        ref={formRef}
        tool={editedTool}
        originalTool={originalTool}
        onToolChange={setEditedTool}
        onSubmit={handleSubmit}
        onCancel={handleNavigateBack}
        isEditing={true}
        readOnly={isReadOnly}
        onEditClick={() => setIsReadOnly(false)}
        onDiscardChanges={handleDiscardChanges}
        hasUnsavedChanges={hasUnsavedChanges}
        setIsReadOnly={setIsReadOnly}
        tagSuggestions={tagSuggestions}
        existingToolNames={existingToolNames}
        currentUserId={user?.sub || ''}
        isSaving={isSaving}
      />

      <NavigationWarningDialog
        open={showNavigationWarning}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
        title="Discard Changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
      />
    </div>
  );
};

export default EditToolPage;